import { Icon, type IconName } from '../../../components/icons/Icon'
import { useWidgetDensity } from '../grid/WidgetDensityContext'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'

export type KpiDeltaVariant = 'favorable' | 'unfavorable' | 'neutral'

const DELTA_ICON: Record<KpiDeltaVariant, IconName> = {
  favorable: 'check-circle',
  unfavorable: 'alert-triangle',
  neutral: 'minus-circle',
}

export interface KpiBreakdownRow {
  key: string
  label: string
  value: string
}

export interface KpiTileProps {
  title: string
  value: string
  /** e.g. "USD", "filings" — shown under the headline number. */
  valueCaption?: string
  delta?: { label: string; variant: KpiDeltaVariant } | undefined
  breakdown: KpiBreakdownRow[]
  height?: number
}

/**
 * AC3 — Claimed-vs-approved variance (data-model §14 #48). The delta glyph
 * uses a status token (never a chart-series token) — CLAUDE.md §4 "status
 * colours are reserved and never reused for series", applied in reverse here
 * (a series token would never stand in for a status either).
 *
 * **Auto-density (IB-311 Slice 1):** "KPI tiles shrink to number+label" (design-system.md §5) —
 * `full` shows value + caption + delta; `compact` drops the caption (value + delta); `micro` drops
 * both and shows the number alone. The full breakdown is never lost — it stays in the
 * `ChartAccessibleTable` behind `ChartFrame`'s "view as table" toggle at every density.
 */
export function KpiTile({ title, value, valueCaption, delta, breakdown, height = 160 }: KpiTileProps) {
  const { density } = useWidgetDensity()
  const table = (
    <ChartAccessibleTable
      caption={`${title} — breakdown`}
      columns={[
        { key: 'label', label: 'Metric' },
        { key: 'value', label: 'Value', align: 'end' },
      ]}
      rows={breakdown.map((row) => ({ label: row.label, value: row.value }))}
    />
  )

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      chart={
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 'var(--iw-space-2)' }}>
          <span className="iw-kpi-value">{value}</span>
          {density === 'full' && valueCaption ? (
            <span style={{ fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>{valueCaption}</span>
          ) : null}
          {density !== 'micro' && delta ? (
            <span className={`iw-kpi-delta iw-kpi-delta--${delta.variant}`}>
              <Icon name={DELTA_ICON[delta.variant]} size={14} />
              {delta.label}
            </span>
          ) : null}
        </div>
      }
    />
  )
}
